import React from 'react';

import { LogOutIcon, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { LogoutConfirmationModal } from '@/modules/common/components/modals/logout-confirmation-modal';

interface FullPageErrorStateProps {
  title: string;
  description: React.ReactNode;
  /** Optional icon rendered via EmptyState */
  icon?: LucideIcon;
  /** Buttons/actions to render below description */
  actions?: React.ReactNode;
  /** Whether to show the absolute-positioned logout button */
  showLogoutButton?: boolean;
}

export const FullPageErrorState: React.FC<FullPageErrorStateProps> = ({
  title,
  description,
  icon: Icon,
  actions,
  showLogoutButton,
}) => {
  const { t } = useTranslation('common');

  return (
    <div className="relative flex min-h-screen items-center justify-center">
      <Empty>
        <EmptyHeader>
          {Icon && (
            <EmptyMedia variant="icon">
              <Icon />
            </EmptyMedia>
          )}
          <EmptyTitle>{title}</EmptyTitle>
          <EmptyDescription>{description}</EmptyDescription>
        </EmptyHeader>
        {actions && <EmptyContent>{actions}</EmptyContent>}
      </Empty>

      {showLogoutButton && (
        <LogoutConfirmationModal
          trigger={
            <Button
              variant="ghost"
              className="absolute bottom-6 left-6 gap-2 text-sm text-muted-foreground"
            >
              <LogOutIcon className="size-4" />
              {t('logout')}
            </Button>
          }
        />
      )}
    </div>
  );
};
